import { Link, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { useMe } from "../../lib/auth";
import { useUpcomingEvents } from "../../lib/events";
import { useArticles } from "../../lib/articles";
import { modalLinkState } from "../../lib/modal-routing";
import { roleLabel } from "../../lib/roles";
import { useDateTimeFormat } from "../../lib/preferences";

export default function DashboardHome() {
  const location = useLocation();
  const { data } = useMe();
  const user = data?.user;
  const dtf = useDateTimeFormat();
  // Two compact overviews under the welcome text — keeps the home page
  // useful instead of just a greeting. Events use the recurrence-expanded
  // upcoming endpoint so weekly classes etc. surface on every occurrence.
  const eventsQ = useUpcomingEvents({ limit: 4, days: 30 });
  const newsQ = useArticles({ limit: 4 });
  const upcoming = eventsQ.data?.events ?? [];
  const news = newsQ.data?.articles ?? [];
  if (!user) return null;

  const lastVisit = user.lastLoginAt ? dtf.date(user.lastLoginAt, { long: true }) : null;
  const memberSince = user.createdAt ? dtf.date(user.createdAt, { long: true }) : null;

  return (
    <div className="container-edit">
      {/* Two-column layout: a flexing main column on the left, a fixed-width
          aside pinned to the right edge of the container. The previous 12-col
          grid centered the whole 1100-px block within the container, so the
          aside sat awkwardly between centre and right; this pattern matches
          how the article editor sidebar sits and reads as truly right-side. */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-12">
        <div>
          <p className="page-eyebrow">Home</p>
          <h1 className="page-title mb-1">
            Welcome, {user.displayName}.
          </h1>

          <div className="mt-4 mb-6 flex items-center gap-4 flex-wrap">
            <span className="role-badge">{roleLabel(user.role)}</span>
            {lastVisit && (
              <span className="font-grotesk text-[0.68rem] tracking-[0.14em] uppercase text-[var(--color-ink-mute)]">
                Last visit · {lastVisit}
              </span>
            )}
          </div>

          <p className="text-[0.96rem] leading-[1.6] text-[var(--color-ink-soft)] max-w-[62ch]">
            You're inside the platform. Use the tabs to manage your articles, browse the
            calendar, or update your profile. New features ship regularly — see what's coming
            next on the right.
          </p>

          {/* Two overview strips: upcoming events on the left, latest news on
              the right. Functional register — grotesk type, compact rows,
              meta in the eyebrow line, link straight through to the detail
              pages. Both strips quietly hide if the data sets are empty so
              an unused calendar / empty news tab doesn't surface dead UI. */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-7">
            <section className="dash-overview" aria-label="Upcoming events">
              <header className="dash-overview-head">
                <p className="page-eyebrow">Upcoming events</p>
                <Link to="/dashboard/calendar" className="dash-overview-more">
                  All →
                </Link>
              </header>
              {upcoming.length === 0 ? (
                <p className="dash-overview-empty">Nothing on the horizon yet.</p>
              ) : (
                <ul className="dash-overview-list">
                  {upcoming.map((ev) => (
                    <li key={ev.occurrenceId}>
                      <Link
                        to={`/dashboard/calendar/${ev.slug}`}
                        state={modalLinkState(location)}
                        className="dash-overview-item"
                      >
                        <span className="dash-overview-when">
                          {format(new Date(ev.startsAt), "EEE d LLL")}
                          {!ev.allDay && (
                            <>
                              <span className="dash-overview-sep">·</span>
                              {ev.timeTbd ? "Time TBA" : format(new Date(ev.startsAt), "HH:mm")}
                            </>
                          )}
                        </span>
                        <span className="dash-overview-title">{ev.title}</span>
                        <span className="dash-overview-meta">
                          {ev.category.replace(/-/g, " ")}
                          {ev.locationType !== "in-person" && (
                            <>
                              <span className="dash-overview-sep">·</span>
                              {ev.locationType}
                            </>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="dash-overview" aria-label="Latest news">
              <header className="dash-overview-head">
                <p className="page-eyebrow">Latest news</p>
                <Link to="/dashboard/articles?view=news" className="dash-overview-more">
                  All →
                </Link>
              </header>
              {news.length === 0 ? (
                <p className="dash-overview-empty">No published articles yet.</p>
              ) : (
                <ul className="dash-overview-list">
                  {news.map((a) => (
                    <li key={a.id}>
                      <Link
                        to={`/news/${a.slug}`}
                        className="dash-overview-item"
                      >
                        <span className="dash-overview-when">
                          {a.publishedAt
                            ? format(new Date(a.publishedAt), "d LLL yyyy")
                            : "Draft"}
                        </span>
                        <span className="dash-overview-title">{a.title}</span>
                        <span className="dash-overview-meta">
                          {a.category.replace(/-/g, " ")}
                          {a.authorName && (
                            <>
                              <span className="dash-overview-sep">·</span>
                              {a.authorName}
                            </>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="veda-panel">
            <p className="page-eyebrow mb-3">Coming next</p>
            <ul className="space-y-2.5 text-[0.9rem] text-[var(--color-ink)]">
              <li className="flex items-baseline gap-3">
                <span className="font-grotesk text-[0.62rem] tracking-[0.18em] text-[var(--color-ink-mute)] tabular" aria-hidden>01</span>
                <span>Document library &amp; reader</span>
              </li>
              <li className="flex items-baseline gap-3">
                <span className="font-grotesk text-[0.62rem] tracking-[0.18em] text-[var(--color-ink-mute)] tabular" aria-hidden>02</span>
                <span>Email verification gate</span>
              </li>
              <li className="flex items-baseline gap-3">
                <span className="font-grotesk text-[0.62rem] tracking-[0.18em] text-[var(--color-ink-mute)] tabular" aria-hidden>03</span>
                <span>Member directory &amp; profiles</span>
              </li>
              <li className="flex items-baseline gap-3">
                <span className="font-grotesk text-[0.62rem] tracking-[0.18em] text-[var(--color-ink-mute)] tabular" aria-hidden>04</span>
                <span>Audio recordings &amp; recitations</span>
              </li>
            </ul>
          </div>

          {memberSince && (
            <p className="mt-4 font-grotesk text-[0.62rem] tracking-[0.16em] uppercase text-[var(--color-ink-light)]">
              Member since {memberSince}
            </p>
          )}

          <div className="mt-6 flex flex-col gap-3 items-start">
            <Link to="/dashboard/profile" className="btn-app-ghost">
              Edit your profile
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
